import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '~/lib/utils';

const buttonVariants = cva(
  'font-greycliff-extrabold inline-flex text-base items-center justify-center gap-2 whitespace-nowrap rounded-full ring-offset-white transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-magenta-dark',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        inverted: 'bg-background text-secondary hover:opacity-95 transition-opacity',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground',
        'outline-secondary':
          'border-2 border-secondary text-secondary  hover:bg-secondary hover:text-secondary-foreground',
        'outline-subtle': 'border-2 hover:text-inverted-foreground border-foreground hover:bg-foreground',
        'outline-inverted': 'border-2 border-background text-background hover:bg-background hover:text-secondary',
        ghost: 'hover:bg-slate-100 hover:text-slate-900',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-12 px-6 pt-1 [&_svg]:size-4',
        sm: 'h-9 text-xs px-4 pt-0.5 [&_svg]:size-3 ',
        lg: 'h-14 px-8 [&_svg]:size-4',
        icon: 'size-12 [&_svg]:size-4',
        'icon-sm': 'size-9 [&_svg]:size-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
